import type { Dispatch, MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  continueRunSections,
  createPlan,
  customDraftSections,
  type DocTypeConfig,
  getDocTypeConfig,
  type ResolvedInventoryDTO,
  sharedClientConfig,
  startRun,
} from "../../api/client";
import { moveDocumentToFolder } from "@shared/client/folders";
import { isPrivateTemplateCode } from "../doc-catalog";
import { libraryDraftReadiness } from "../library-hub/mergeUploadsAndInventory";
import { refusalMessage } from "../payments/paywall";
import { pauseSync } from "../../services/syncEngine";
import {
  clearWordHydrationMarker,
  loadWordHydrationMarker,
  readCustomSpineOutline,
  saveWordInsertionMode,
  setActiveRunId,
  setDraftPointer,
} from "../../services/wordWriter";
import type { CustomSpineSection, StartedPaidDraft } from "../../api/client";
import type { LoomFreshSelection, SectionItem } from "../components/LoomFresh";
import { suggestedDraftName } from "../draftFlow";
import type { PlanData } from "../components/LoomPlanReview";
import type { LoomSelectionCommit } from "../components/LoomSelection";
import { shouldLiveProject } from "../liveProjection";
import { moduleIdsToGateNames, sectionIdsToModuleIds } from "../selection";
import { getCachedDocId, scheduleSave } from "../services/documentSessionService";
import type { VariantCEvent } from "../state";
import { spineSectionsFromConfig, synthesizeProjectName } from "../utils/spineSnapshot";
import { computeLockedModuleIds, type SpineSection } from "./useVariantCRun";

export function commitStrategyToInsertionMode(strategy: LoomSelectionCommit["strategy"]): "section" | "batch" {
  return strategy === "batched" ? "batch" : "section";
}

export interface UseDraftFlowParams {
  runId: string | null;
  // From shell state
  activeSelection: LoomFreshSelection | null;
  setActiveSelection: (s: LoomFreshSelection | null) => void;
  docConfig: DocTypeConfig | null;
  setDocConfig: (c: DocTypeConfig | null) => void;
  /** Owned by shell so useSessionBootstrap can seed it from hydrate. */
  sectionCommit: LoomSelectionCommit | null;
  setSectionCommit: (c: LoomSelectionCommit | null) => void;
  /** Owned by shell so useSessionBootstrap can seed it from hydrate. */
  planId: string | null;
  setPlanId: (id: string | null) => void;
  /** Owned by shell so useSessionBootstrap can seed it from hydrate. */
  planData: PlanData | null;
  setPlanData: (p: PlanData | null) => void;
  // Run-start side effects (stay in shell; also used by pause/resume/reset)
  setStarting: (v: boolean) => void;
  setStartError: (e: string | null) => void;
  setRunSubscriptionStartMode: (m: "history" | "latest") => void;
  /** Seeds the next (re)subscribe's resume position — a continuation replays
   *  from the cursor continue-sections returns instead of tailing "latest". */
  seedStreamCursor?: (id: string | null) => void;
  /** Clears the terminal-run SSE gate so ``useVariantCRun`` re-subscribes
   *  to the brand-new run's stream. After reopening a completed run this
   *  flag is true (bootstrap-rehydration sets it) and would otherwise
   *  short-circuit the subscribe effect before the new run's events land —
   *  the "Draft more sections" failure mode. Mirrors the regen flow. */
  setTerminalRunStatus: (v: boolean) => void;
  /** Bumps the SSE subscription nonce to force a fresh subscribe. Paired
   *  with ``setTerminalRunStatus`` above for parity with ``useRegenFlow``. */
  setRunSubscriptionNonce: (fn: (v: number) => number) => void;
  /** Bumps the boot-seed nonce so the spine shows the "Preparing run" card
   *  while the drafting child boots — for continuations too, which subscribe
   *  in ``latest`` mode and so miss the fresh-run seed. Optional for callers
   *  (tests) that don't render the spine. */
  setRunBootSeedNonce?: (fn: (v: number) => number) => void;
  setRunStartedAt: (t: number | null) => void;
  dispatch: Dispatch<VariantCEvent>;
  // From useWordWriteQueue (reset at run-start time)
  scaffoldedRef: MutableRefObject<boolean>;
  insertionModeRef: MutableRefObject<"section" | "batch">;
  /** Whether this run scaffolds the skeleton + receives live per-section
   *  writes. Set from the doc-type manifest at start; deliberately SEPARATE
   *  from ``insertionModeRef``, which stays "batch" so the run survives the
   *  writer closing the task pane. */
  liveProjectionRef: MutableRefObject<boolean>;
  // From useWorkspaceLibrary (threaded into startRun payload)
  libraryInventory: ResolvedInventoryDTO | null;
  // From useVariantCRun (needed by handleDraftMoreSections)
  sections: SpineSection[];
  /** Submission mode of the loaded run ("custom" for build-on-existing-doc).
   *  When "custom", a "Draft more" / "Generate" continue dispatches through the
   *  custom-draft endpoint (which drafts CUSTOM-N sections from their saved
   *  instructions) instead of continue-sections (template module ids only). */
  submissionMode?: string | null;
  /**
   * Gate a fresh run behind payment. Called just before the free `startRun`
   * path, only for a NEW run (never "Draft more"). Returns `true` to proceed
   * free (demo/exempt workspace, or no price), or `false` when it has diverted
   * the start into a paid checkout — in which case the run is started later by
   * `finalizePaidRunStart` and this call must NOT start one. Owned by the shell,
   * which holds the checkout hook and dialogs. Absent → always free.
   */
  paymentGate?: (info: {
    selection: LoomFreshSelection;
    moduleIds: string[] | null;
    continueRunId: string | null;
    /** The writer's picker comments, carried onto the paid run. */
    runInstructions: string | null;
    /**
     * Section numbers just committed in the picker. Passed because the shell's
     * `sectionCommit` state is set in the same tick and its gate closure still
     * holds the previous (often null) commit.
     */
    sectionIds?: readonly string[];
  }) => Promise<boolean>;
  /**
   * Saves the live document (the shell's Save). Awaited before a "Draft more"
   * that redrafts already-drafted sections, so the server's record of each
   * section — which a redraft archives and carries its tables and figures over
   * from — is what the writer sees in Word, not the last auto-save.
   */
  saveBeforeRedraft?: () => Promise<void>;
}

export interface UseDraftFlowResult {
  planLoading: boolean;
  planError: string | null;
  draftingMore: boolean;
  lockedModuleIdsForNextPlan: ReadonlyArray<string> | null;
  sectionPickerActive: boolean;
  pendingDraftStart: boolean;
  setPendingDraftStart: (v: boolean) => void;
  planApiHealthy: boolean;
  /** Reset all draft-flow state back to idle. Call from handleReset / handleStopRun. */
  resetDraftFlowState: () => void;
  handleFetchSections: (selection: LoomFreshSelection) => Promise<SectionItem[]>;
  handleStartDraft: (selection: LoomFreshSelection) => Promise<void>;
  handlePlanApprove: (moduleIds: string[]) => void;
  handlePlanSkip: () => void;
  handlePlanModify: () => void;
  handleSectionCommit: (commit: LoomSelectionCommit) => Promise<void>;
  handleSectionCancel: () => void;
  handleDraftMoreSections: () => void;
  /** Run-start primitive — exposed so the regen flow can re-use the
   *  same `POST /api/runs` path with `module_ids` scoped to one section. */
  /** Draft a single undrafted section immediately, no picker. Used by the
   *  Inspector's "Generate" button. */
  handleGenerateSection: (sectionId: string) => Promise<void>;
  actuallyStartRun: (
    selection: LoomFreshSelection,
    runPlanId: string | null,
    moduleIds: string[] | null,
    continueRunId?: string | null,
    opts?: { skipPaymentGate?: boolean; runInstructions?: string | null },
  ) => Promise<void>;
  /** Replay the post-start side-effects for a run the PAID checkout already
   *  started server-side (`POST /billing/intents/{ref}/draft`). Never calls
   *  `startRun` — the run exists — so the buyer can never be double-started.
   *  ``moduleIds`` is the paid run's section scope (null = whole document). */
  finalizePaidRunStart: (started: StartedPaidDraft, moduleIds?: readonly string[] | null) => Promise<void>;
  /** Build-on-existing-document: register a custom-submission run from the live
   *  Word doc's heading outline. Returns the new run_id AND the outline that was
   *  read (so the caller can render the spine from it directly, without waiting
   *  on the server snapshot round-trip), or null on failure. ``onStep`` fires as
   *  the two internal phases complete so a progress card can advance. */
  startCustomSubmission: (
    selection: LoomFreshSelection,
    onStep?: (step: "outline" | "project") => void,
  ) => Promise<CustomSubmissionResult | null>;
}

/** Result of {@link useDraftFlow}'s ``startCustomSubmission``. */
export interface CustomSubmissionResult {
  runId: string;
  /** The document heading outline read from the live Word body, in document
   *  order, each row carrying its rolled-up ``has_content`` flag. */
  outline: CustomSpineSection[];
}

/** Bind the Word doc to a just-started run: overwrite the stamped active run
 *  id and drop a hydration marker that names a different (older) run. Both
 *  survive in the doc's custom props; left stale, a refresh or re-login
 *  mid-run restored the PREVIOUS run (Pause/Kill gone) instead of this one.
 *  Best-effort — a Word failure never fails the run start. */
export async function rebindWordToRun(runId: string): Promise<void> {
  try {
    await setActiveRunId(runId);
    const marker = await loadWordHydrationMarker();
    if (marker && marker.runId !== runId) await clearWordHydrationMarker();
  } catch (err) {
    console.warn("[useDraftFlow] could not re-stamp the Word doc with the new run id:", err);
  }
}

export function useDraftFlow({
  runId,
  activeSelection,
  setActiveSelection,
  docConfig,
  setDocConfig,
  sectionCommit,
  setSectionCommit,
  planId,
  setPlanId,
  planData,
  setPlanData,
  setStarting,
  setStartError,
  setRunSubscriptionStartMode,
  seedStreamCursor,
  setTerminalRunStatus,
  setRunSubscriptionNonce,
  setRunBootSeedNonce,
  setRunStartedAt,
  dispatch,
  scaffoldedRef,
  insertionModeRef,
  liveProjectionRef,
  libraryInventory,
  sections,
  submissionMode,
  paymentGate,
  saveBeforeRedraft,
}: UseDraftFlowParams): UseDraftFlowResult {
  // Read at start time without re-creating the start callback on every SSE.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  // Phase 9.4 — "Draft more sections" mode.
  const [draftingMore, setDraftingMore] = useState(false);
  const [lockedModuleIdsForNextPlan, setLockedModuleIdsForNextPlan] = useState<ReadonlyArray<string> | null>(null);

  const [sectionPickerActive, setSectionPickerActive] = useState(false);

  // Conversational /draft flow. true = /draft fired, orchestrator is
  // checking what's still missing. Escape dismisses it without wiping
  // doctype/library picks (they're durable standalone selections).
  const [pendingDraftStart, setPendingDraftStart] = useState<boolean>(false);
  const startRequestInFlightRef = useRef(false);
  // The writer's final comments from the last section-picker commit. Read by
  // ``actuallyStartRun`` (directly, or after the plan review / a promo re-drive)
  // and cleared once a start is attempted, so one batch's comments never ride
  // along on a later start that didn't come from the picker.
  const runInstructionsRef = useRef<string | null>(null);

  const [planApiHealthy, setPlanApiHealthy] = useState(true);

  // Cached gate names for the active selection — derived once at
  // plan-fetch time and reused on Approve/Skip without re-reading docConfig.
  const planGateNamesRef = useRef<string[]>([]);

  // Escape dismisses an in-flight /draft without clearing existing picks.
  useEffect(() => {
    if (!pendingDraftStart) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setPendingDraftStart(false);
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [pendingDraftStart]);

  // When the writer proceeds past chat orchestration (sectionPickerActive
  // becomes true), clear the pendingDraftStart flag.
  useEffect(() => {
    if (sectionPickerActive && pendingDraftStart) {
      setPendingDraftStart(false);
    }
  }, [sectionPickerActive, pendingDraftStart]);

  // -- Section fetch (step 5) -------------------------------------------
  const handleFetchSections = useCallback(async (selection: LoomFreshSelection): Promise<SectionItem[]> => {
    const config = await getDocTypeConfig(selection.backendDocType);
    return spineSectionsFromConfig(config).map((s) => ({
      moduleId: s.id,
      sectionNumber: s.number,
      title: s.title,
    }));
  }, []);

  // -- Core start-run primitive -----------------------------------------
  // biome-ignore lint/correctness/useExhaustiveDependencies: libraryInventory intentionally omitted
  const actuallyStartRun = useCallback(
    async (
      selection: LoomFreshSelection,
      runPlanId: string | null,
      moduleIds: string[] | null,
      continueRunId: string | null = null,
      opts: { skipPaymentGate?: boolean; runInstructions?: string | null; sectionIds?: readonly string[] } = {},
    ) => {
      if (startRequestInFlightRef.current) {
        return;
      }
      // An explicit value (including null) wins over the picker's comments.
      const runInstructions =
        (opts.runInstructions !== undefined ? opts.runInstructions : runInstructionsRef.current)?.trim() || null;
      // Parse-readiness gate — don't start a run while any source
      // document is still processing, has failed, or was structurally
      // analysed but has no queryable text (``limited``). Drafting from
      // such a library retrieves nothing useful. Checked here because
      // ``actuallyStartRun`` is the single primitive every draft entry
      // point (fresh draft, plan approve/skip, section commit, "Draft
      // more sections", single-section "Generate") funnels through.
      const readiness = libraryDraftReadiness(libraryInventory);
      if (!readiness.ready) {
        const names = readiness.blocking.map((b) => b.filename).join(", ");
        const count = readiness.blocking.length;
        const message =
          `Can't draft yet — ${count} source document${count === 1 ? "" : "s"} ` +
          `${count === 1 ? "isn't" : "aren't"} fully processed: ${names}. ` +
          `Wait for parsing to finish (or remove failed documents from the library) and try again.`;
        setStartError(message);
        dispatch({
          type: "RUN_FAILED",
          error: { code: "library_not_ready", message, recoverable: true },
        });
        return;
      }
      // A NEW run must be named — on the section picker (or, for a build-on
      // run, the shell's name prompt), so this only catches an entry point that
      // skipped both. A continuation keeps the name its run already has.
      if (!continueRunId && !selection.name?.trim()) {
        setStartError("Name this draft before starting it.");
        return;
      }
      // Payment gate — a draft in a billable (custom) workspace diverts into a
      // paid checkout instead of starting for free. Runs BEFORE we pause sync or
      // take the draft pointer, so a diverted start leaves the document untouched
      // while the order dialog is open. Gates a fresh run AND a "Draft more"
      // continuation — unlocking a section is paying for it — but not a custom
      // submission, whose flat unlock is charged separately (see the shell's
      // ``maybeGateCustom``) and whose CUSTOM-N sections were never priced. The
      // paid run is finished by ``finalizePaidRunStart``.
      if (paymentGate && submissionMode !== "custom" && !opts.skipPaymentGate) {
        const proceedFree = await paymentGate({
          selection,
          moduleIds,
          continueRunId,
          runInstructions,
          sectionIds: opts.sectionIds,
        });
        if (!proceedFree) return;
      }
      startRequestInFlightRef.current = true;
      setStarting(true);
      setStartError(null);
      try {
        // Redrafting drafted sections: save Word first (see saveBeforeRedraft).
        const drafted = new Set(computeLockedModuleIds(sectionsRef.current));
        if (continueRunId && saveBeforeRedraft && (moduleIds ?? []).some((id) => drafted.has(id))) {
          try {
            await saveBeforeRedraft();
          } catch (err) {
            // handleSave shows its own error; the redraft still starts.
            console.warn("[useDraftFlow] save before redraft failed:", err);
          }
        }
        // The server now owns the document for the duration of this run. Pause
        // live sync immediately (in-memory) and persist ownership as the draft
        // pointer, so the add-in never pushes the Word body over the server's
        // concurrent writes — which would tangle the DocService block DLL and
        // scramble the document on load. The pointer also survives a task-pane
        // reload mid-run. Flipped back to "live" by handleLoadDraftedDocument.
        pauseSync();
        await setDraftPointer("server");
        const projectName = synthesizeProjectName(selection);
        const result = continueRunId
          ? await (async () => {
              if (!moduleIds || moduleIds.length === 0) {
                throw new Error("Draft more requires at least one section to continue.");
              }
              // Custom submission: CUSTOM-N headings aren't template module ids,
              // so continue-sections would 400 on them. Route to the custom-draft
              // endpoint, which drafts each section from its saved instruction.
              if (submissionMode === "custom") {
                return customDraftSections(continueRunId, {
                  module_ids: moduleIds,
                  plan_id: runPlanId ?? undefined,
                  run_instructions: runInstructions ?? undefined,
                });
              }
              return continueRunSections(continueRunId, {
                module_ids: moduleIds,
                plan_id: runPlanId ?? undefined,
                run_instructions: runInstructions ?? undefined,
              });
            })()
          : await startRun({
              project_name: projectName,
              name: selection.name?.trim() || undefined,
              doc_id: getCachedDocId() || undefined,
              library_ids: [selection.libraryId],
              library_names: { [selection.libraryId]: selection.libraryName },
              doc_type: selection.backendDocType,
              workspace_id: selection.workspaceId ?? undefined,
              region: selection.region || undefined,
              gates: planGateNamesRef.current.length > 0 ? planGateNamesRef.current : undefined,
              module_ids: moduleIds && moduleIds.length > 0 ? moduleIds : undefined,
              plan_id: runPlanId ?? undefined,
              run_instructions: runInstructions ?? undefined,
              review: true,
              insertion_mode_override: "batch",
              library_inventories: libraryInventory ? { [selection.libraryId]: libraryInventory } : undefined,
            });
        insertionModeRef.current = "batch";
        void saveWordInsertionMode("batch");
        // Live section projection is decided by the doc-type manifest and is
        // deliberately NOT the insertion mode above: that stays "batch" so the
        // run survives the writer closing the task pane (useSessionBootstrap)
        // and so restore never force-clears the body (useRunControl).
        liveProjectionRef.current = shouldLiveProject(docConfig, submissionMode);
        // A continuation ("Draft more sections" / single-section "Generate")
        // writes into a document that was already scaffolded — pre-arm the
        // idempotency guard so gate_start never rebuilds the skeleton over
        // drafted content. A fresh run leaves it false so the scaffold runs.
        scaffoldedRef.current = continueRunId !== null;
        setRunSubscriptionStartMode(continueRunId ? "latest" : "history");
        // Replay from the cursor captured before dispatch: tailing "latest"
        // races-missed this dispatch's first module_start, so a section
        // drafted on a reopened run showed no progress until a pause.
        if (continueRunId) {
          const cursor = (result as { stream_cursor?: string | null }).stream_cursor;
          if (cursor) seedStreamCursor?.(cursor);
        }
        // A brand-new run always has a live Redis stream. Clear the
        // terminal-run gate (set true when a completed run was restored on
        // reopen) and bump the subscription nonce so useVariantCRun's SSE
        // subscribe effect re-attaches instead of short-circuiting. Without
        // this, "Draft more sections" after reopening a finished run leaves
        // the spine frozen (no live events, no snapshot poll). Mirrors the
        // regen flow's gate-clear in useRegenFlow.
        setTerminalRunStatus(false);
        setRunBootSeedNonce?.((n) => n + 1);
        setRunSubscriptionNonce((n) => n + 1);
        dispatch({ type: continueRunId ? "RUN_CONTINUED" : "RUN_STARTED", runId: result.run_id, moduleIds });
        void rebindWordToRun(result.run_id);
        setRunStartedAt(Date.now());
        scheduleSave(getCachedDocId(), {
          run_id: result.run_id,
          insertion_mode: "batch",
        });
        // File the new document into the folder the writer chose at creation —
        // strictly per-document (this doc only, leaf or not). Only on a fresh
        // run (a "draft more" continue keeps the doc where it is), and
        // best-effort: a failed filing is a mis-filed card the writer can move
        // on Home, never a reason to fail the run they just started.
        if (!continueRunId && selection.folderId) {
          const docId = getCachedDocId();
          if (docId) {
            void moveDocumentToFolder(sharedClientConfig(), docId, selection.folderId).catch((err: unknown) => {
              console.warn("[useDraftFlow] could not file new document into folder:", err);
            });
          }
        }
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: Date.now(),
            actor: "asthra",
            sectionId: null,
            runId: result.run_id,
            payload: { kind: "run_start" },
          },
        });
        // Plan state intentionally preserved for "Draft more sections" reuse.
      } catch (err) {
        // A custom document that is not unlocked puts the unlock up, and the
        // same "Draft more" is re-sent once it clears.
        const message = refusalMessage(
          err,
          () => void actuallyStartRun(selection, runPlanId, moduleIds, continueRunId, opts),
          selection.backendDocType,
        );
        setStartError(message);
        dispatch({
          type: "RUN_FAILED",
          error: { code: "start_run_failed", message, recoverable: true },
        });
      } finally {
        startRequestInFlightRef.current = false;
        runInstructionsRef.current = null;
        setStarting(false);
      }
    },
    // libraryInventory intentionally omitted — snapshot at call time is
    // sufficient; adding it would re-create the callback on every fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setStarting,
      setStartError,
      setRunSubscriptionStartMode,
      seedStreamCursor,
      setTerminalRunStatus,
      setRunSubscriptionNonce,
      setRunBootSeedNonce,
      setRunStartedAt,
      runId,
      dispatch,
      docConfig,
      submissionMode,
      paymentGate,
      saveBeforeRedraft,
    ],
  );

  // -- finalizePaidRunStart ----------------------------------------------
  // A paid run is created server-side by the checkout (`startPaidDraft`), so the
  // add-in never calls `startRun` for it. This replays the same post-start
  // side-effects `actuallyStartRun` runs once a run id is known — the SSE
  // re-subscribe, the server draft-pointer, the batch insertion mode, and the
  // dispatch that flips the shell into a live run — for that already-started run.
  const finalizePaidRunStart = useCallback(
    async (started: StartedPaidDraft, moduleIds: readonly string[] | null = null) => {
      // The paid run carried the picker comments; don't let them reach a later start.
      runInstructionsRef.current = null;
      // The server owns the document now — mirror the free path's ownership
      // handoff so the add-in never pushes the Word body over the server's writes.
      pauseSync();
      await setDraftPointer("server");
      insertionModeRef.current = "batch";
      void saveWordInsertionMode("batch");
      // Mirror the free path: a paid run projects live on the same terms.
      liveProjectionRef.current = shouldLiveProject(docConfig, submissionMode);
      scaffoldedRef.current = false;
      // A freshly-started paid run has a live stream, same as a free fresh run.
      setRunSubscriptionStartMode("history");
      setTerminalRunStatus(false);
      setRunBootSeedNonce?.((n) => n + 1);
      setRunSubscriptionNonce((n) => n + 1);
      dispatch({ type: "RUN_STARTED", runId: started.run_id, moduleIds });
      void rebindWordToRun(started.run_id);
      setRunStartedAt(Date.now());
      scheduleSave(started.doc_id, {
        run_id: started.run_id,
        insertion_mode: "batch",
      });
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "asthra",
          sectionId: null,
          runId: started.run_id,
          payload: { kind: "run_start" },
        },
      });
    },
    [
      insertionModeRef,
      liveProjectionRef,
      scaffoldedRef,
      docConfig,
      submissionMode,
      setRunSubscriptionStartMode,
      setTerminalRunStatus,
      setRunSubscriptionNonce,
      setRunBootSeedNonce,
      setRunStartedAt,
      dispatch,
    ],
  );

  // -- startCustomSubmission ---------------------------------------------
  // Build-on-existing-document: register a run over a Word doc the writer
  // already had. Reads the doc's heading outline into a custom spine and
  // creates a ``submission_mode: "custom"`` run WITHOUT dispatching the
  // drafting worker (the document already has content). Sync is intentionally
  // left LIVE — the writer keeps editing in Word; the server never owns the
  // body. Returns the new run_id (or null on failure) so the shell can load
  // it through the normal snapshot-hydration path.
  const startCustomSubmission = useCallback(
    async (
      selection: LoomFreshSelection,
      onStep?: (step: "outline" | "project") => void,
    ): Promise<CustomSubmissionResult | null> => {
      if (startRequestInFlightRef.current) return null;
      if (!selection.name?.trim()) {
        setStartError("Name this draft before starting it.");
        return null;
      }
      // A source library is OPTIONAL for a custom submission (the writer may
      // skip it — the audit library is later bootstrapped from the document's
      // own content). Only enforce the parse-readiness gate when a library was
      // actually chosen.
      const hasLibrary = Boolean(selection.libraryId);
      if (hasLibrary) {
        const readiness = libraryDraftReadiness(libraryInventory);
        if (!readiness.ready) {
          const names = readiness.blocking.map((b) => b.filename).join(", ");
          const count = readiness.blocking.length;
          setStartError(
            `Can't connect the library yet — ${count} source document${count === 1 ? "" : "s"} ` +
              `${count === 1 ? "isn't" : "aren't"} fully processed: ${names}.`,
          );
          return null;
        }
      }
      startRequestInFlightRef.current = true;
      setStarting(true);
      setStartError(null);
      try {
        const outline = await readCustomSpineOutline();
        if (outline.length === 0) {
          setStartError("No headings found in this document — add heading styles so the outline can be read.");
          return null;
        }
        // Outline read → project registration begins.
        onStep?.("outline");
        const projectName = synthesizeProjectName(selection);
        const result = await startRun({
          project_name: projectName,
          name: selection.name?.trim() || undefined,
          doc_id: getCachedDocId() || undefined,
          // Empty when the writer skipped the library step — the server accepts a
          // custom run with no source library and bootstraps the audit library
          // from the document's own content on first review.
          library_ids: hasLibrary ? [selection.libraryId] : [],
          library_names: hasLibrary ? { [selection.libraryId]: selection.libraryName } : {},
          doc_type: selection.backendDocType,
          workspace_id: selection.workspaceId ?? undefined,
          region: selection.region || undefined,
          submission_mode: "custom",
          custom_outline: outline,
          review: false,
          library_inventories: hasLibrary && libraryInventory ? { [selection.libraryId]: libraryInventory } : undefined,
        });
        scheduleSave(getCachedDocId(), { run_id: result.run_id });
        void rebindWordToRun(result.run_id);
        // Run registered server-side.
        onStep?.("project");
        return { runId: result.run_id, outline };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setStartError(message);
        return null;
      } finally {
        startRequestInFlightRef.current = false;
        setStarting(false);
      }
    },
    // libraryInventory intentionally omitted — snapshot at call time suffices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setStarting, setStartError],
  );

  // -- fetchPlanFor -------------------------------------------------------
  const fetchPlanFor = useCallback(
    async (selection: LoomFreshSelection, moduleIds: string[] | null, continueRunId: string | null = null) => {
      setPlanLoading(true);
      setPlanData(null);
      setPlanId(null);
      setPlanError(null);
      try {
        const projectName = synthesizeProjectName(selection);
        const narrowedGates =
          moduleIds && moduleIds.length > 0 && docConfig
            ? moduleIdsToGateNames(docConfig, moduleIds)
            : planGateNamesRef.current;
        const planResult = await createPlan({
          project_name: projectName,
          doc_id: getCachedDocId() || undefined,
          library_ids: [selection.libraryId],
          library_names: { [selection.libraryId]: selection.libraryName },
          doc_type: selection.backendDocType,
          workspace_id: selection.workspaceId ?? undefined,
          gates: narrowedGates.length > 0 ? narrowedGates : undefined,
          module_ids: moduleIds && moduleIds.length > 0 ? moduleIds : undefined,
          insertion_mode_override: "batch",
        });

        if (!planResult.plan_enabled) {
          setPlanLoading(false);
          await actuallyStartRun(selection, null, moduleIds, continueRunId);
          return;
        }

        setPlanApiHealthy(planResult.api_healthy);
        if (planResult.error || !planResult.plan || !planResult.plan_id) {
          setPlanError(planResult.error ?? "Planner returned no plan.");
        } else {
          setPlanData(planResult.plan as unknown as PlanData);
          setPlanId(planResult.plan_id);
          scheduleSave(getCachedDocId(), { plan_id: planResult.plan_id });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setPlanError(message);
        setPlanApiHealthy(false);
      } finally {
        setPlanLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [docConfig, setPlanData, setPlanId, actuallyStartRun],
  );

  // -- handleStartDraft ---------------------------------------------------
  const handleStartDraft = useCallback(
    async (selection: LoomFreshSelection) => {
      setActiveSelection(selection);
      scheduleSave(getCachedDocId(), {
        plan_id: null,
        run_id: null,
        conversation_id: null,
        spine_snapshot: null,
        section_commit: null,
      });
      setStartError(null);
      setPlanLoading(false);
      setPlanData(null);
      setPlanId(null);
      setPlanError(null);
      setPlanApiHealthy(true);
      setSectionCommit(null);
      runInstructionsRef.current = null;
      scaffoldedRef.current = false;
      insertionModeRef.current = "batch";
      // Custom submissions draft into the writer's OWN document: nothing to
      // scaffold, and a synthetic ``CUSTOM-N`` number matches no heading, so a
      // live write would no-op. Their regen/audit re-projection uses the
      // customSectionNumber / customSectionTitle overrides instead.
      liveProjectionRef.current = false;
      void saveWordInsertionMode("batch");

      const preselectedModules =
        selection.selectedModuleIds && selection.selectedModuleIds.length > 0 ? selection.selectedModuleIds : null;
      if (preselectedModules) {
        // reserved for future tree pre-check
      }

      let config: DocTypeConfig | null = null;
      const privateTemplate = isPrivateTemplateCode(selection.backendDocType);
      // A private workspace template has no bundled fallback: without its
      // config there is no skeleton to scaffold into Word, so one retry, then
      // stop with an error instead of drafting into a blank placeholder.
      for (let attempt = 0; attempt < (privateTemplate ? 2 : 1) && config === null; attempt += 1) {
        try {
          config = await getDocTypeConfig(
            selection.backendDocType,
            privateTemplate ? (selection.workspaceId ?? null) : null,
          );
          setDocConfig(config);
        } catch (err) {
          console.warn(
            "[useDraftFlow] No backend config for doc_type %s; cannot show " +
              "section picker (starting with full-doctype defaults instead).",
            selection.backendDocType,
            err,
          );
        }
      }
      if (privateTemplate && (!config || (config.skeleton?.length ?? 0) === 0)) {
        setStartError("Couldn't load this workspace template's outline. Check you still have access to it, then try again.");
        return;
      }
      planGateNamesRef.current = (config?.gates ?? []).map((g) => g.name);

      if (!config || (config.skeleton?.length ?? 0) === 0) {
        console.warn("[useDraftFlow] Skipping LoomSelection (no config); deferring " + "to legacy full-plan path.");
        // The picker is where a new draft is named; without it, use the
        // suggested name (renamable on Home).
        const named = selection.name?.trim() ? selection : { ...selection, name: suggestedDraftName(selection) };
        if (named !== selection) setActiveSelection(named);
        await fetchPlanFor(named, null);
        return;
      }
      setSectionPickerActive(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      setActiveSelection,
      setStartError,
      setSectionCommit,
      setDocConfig,
      scaffoldedRef,
      insertionModeRef,
      liveProjectionRef,
      fetchPlanFor,
      setPlanData,
      setPlanId,
    ],
  );

  // -- Plan-review handlers -----------------------------------------------
  const handlePlanApprove = useCallback(
    (moduleIds: string[]) => {
      if (!activeSelection) return;
      const continueRunId = draftingMore ? runId : null;
      setDraftingMore(false);
      setLockedModuleIdsForNextPlan(null);
      actuallyStartRun(activeSelection, planId, moduleIds, continueRunId);
    },
    [activeSelection, draftingMore, runId, planId, actuallyStartRun],
  );

  const handlePlanSkip = useCallback(() => {
    if (!activeSelection) return;
    const continueRunId = draftingMore ? runId : null;
    setDraftingMore(false);
    setLockedModuleIdsForNextPlan(null);
    const moduleIds =
      sectionCommit && docConfig ? sectionIdsToModuleIds(docConfig, sectionCommit.resolvedSectionIds) : null;
    actuallyStartRun(activeSelection, null, moduleIds, continueRunId);
  }, [activeSelection, draftingMore, runId, sectionCommit, docConfig, actuallyStartRun]);

  const handlePlanModify = useCallback(() => {
    setDraftingMore(false);
    setLockedModuleIdsForNextPlan(null);
    setPlanData(null);
    setPlanId(null);
    setPlanError(null);
    setSectionPickerActive(true);
  }, [setPlanData, setPlanId]);

  // -- LoomSelection handlers (Spec 23 P10.3) ----------------------------
  const handleSectionCommit = useCallback(
    async (commit: LoomSelectionCommit) => {
      if (!activeSelection || !docConfig) return;
      // A new document is named (and filed) on the picker itself.
      const selection: LoomFreshSelection = commit.name
        ? {
            ...activeSelection,
            name: commit.name,
            folderId: commit.folderId !== undefined ? commit.folderId : activeSelection.folderId,
          }
        : activeSelection;
      if (selection !== activeSelection) setActiveSelection(selection);
      insertionModeRef.current = "batch";
      void saveWordInsertionMode("batch");
      setSectionPickerActive(false);
      setSectionCommit(commit);
      runInstructionsRef.current = commit.runInstructions?.trim() || null;
      scheduleSave(getCachedDocId(), {
        section_commit: {
          resolvedSectionIds: commit.resolvedSectionIds,
          explicitSectionIds: commit.explicitSectionIds,
          strategy: commit.strategy,
          showPlan: commit.showPlan,
        },
        insertion_mode: "batch",
      });

      const moduleIds = sectionIdsToModuleIds(docConfig, commit.resolvedSectionIds);
      const continueRunId = draftingMore ? runId : null;

      if (commit.showPlan) {
        await fetchPlanFor(selection, moduleIds, continueRunId);
      } else {
        await actuallyStartRun(selection, null, moduleIds, continueRunId, {
          sectionIds: commit.resolvedSectionIds,
        });
      }
    },
    [
      activeSelection,
      setActiveSelection,
      docConfig,
      draftingMore,
      runId,
      insertionModeRef,
      setSectionCommit,
      fetchPlanFor,
      actuallyStartRun,
    ],
  );

  const handleSectionCancel = useCallback(() => {
    setSectionPickerActive(false);
    if (draftingMore) {
      setDraftingMore(false);
      setLockedModuleIdsForNextPlan(null);
      return;
    }
    setActiveSelection(null);
    setDocConfig(null);
    setSectionCommit(null);
    planGateNamesRef.current = [];
  }, [draftingMore, setActiveSelection, setDocConfig, setSectionCommit]);

  // -- Phase 9.4: Draft more sections ------------------------------------
  const handleDraftMoreSections = useCallback(() => {
    if (!activeSelection) return;
    setLockedModuleIdsForNextPlan(computeLockedModuleIds(sections));
    setDraftingMore(true);
    // Clear previous section commit so the picker defaults to all sections
    // (not pre-populated with previously drafted ones). This ensures the
    // picker behaves like a fresh draft flow.
    setSectionCommit(null);
    if (!planData) {
      setSectionPickerActive(true);
    }
  }, [planData, activeSelection, sections, setSectionCommit]);

  // -- Generate a single undrafted section (no picker) --------------------
  // Same underlying primitive as "Draft more sections" / handleSectionCommit
  // (actuallyStartRun -> continueRunSections with module_ids), but scoped to
  // exactly one section id and started immediately, mirroring the
  // Regenerate flow's "no picker, no plan review" behavior.
  const handleGenerateSection = useCallback(
    async (moduleId: string) => {
      if (!activeSelection || !docConfig) return;
      insertionModeRef.current = "batch";
      void saveWordInsertionMode("batch");
      setLockedModuleIdsForNextPlan(computeLockedModuleIds(sections));
      // `moduleId` here is a SpineSection.id, which spineSectionsFromConfig
      // sets to the module id itself (not a leaf section id) — pass it
      // straight through as module_ids. sectionIdsToModuleIds is for
      // translating LEAF section ids (e.g. "2.1") from LoomSelection's
      // tree picker and doesn't apply here.
      const moduleIds = [moduleId];
      const continueRunId = runId;
      // Skip the client payment gate here: this button targets a single section
      // already within the run's paid scope, and it carries no fresh
      // ``sectionCommit`` for the gate to price from — the server gate remains
      // the backstop for anything genuinely unpaid.
      await actuallyStartRun(activeSelection, null, moduleIds, continueRunId, {
        skipPaymentGate: true,
        // No picker, so no comments — never reuse a previous batch's.
        runInstructions: null,
      });
    },
    [activeSelection, docConfig, insertionModeRef, sections, runId, actuallyStartRun],
  );

  // -- Reset all draft-flow state (called by handleReset / handleStopRun) -
  const resetDraftFlowState = useCallback(() => {
    setPlanLoading(false);
    setPlanData(null);
    setPlanId(null);
    setPlanError(null);
    setPlanApiHealthy(true);
    setSectionPickerActive(false);
    setDraftingMore(false);
    setLockedModuleIdsForNextPlan(null);
    planGateNamesRef.current = [];
    runInstructionsRef.current = null;
  }, [setPlanData, setPlanId]);

  return {
    planLoading,
    planError,
    draftingMore,
    lockedModuleIdsForNextPlan,
    sectionPickerActive,
    pendingDraftStart,
    setPendingDraftStart,
    planApiHealthy,
    resetDraftFlowState,
    handleFetchSections,
    handleStartDraft,
    handlePlanApprove,
    handlePlanSkip,
    handlePlanModify,
    handleSectionCommit,
    handleSectionCancel,
    handleDraftMoreSections,
    handleGenerateSection,
    actuallyStartRun,
    finalizePaidRunStart,
    startCustomSubmission,
  };
}
